// src/pages/Users/Vote/ParticipateVoteModal.jsx
import React, { useEffect, useState } from "react";
import { getVoteById, submitVote } from "../../../services/voteServices";
import { toast } from "react-toastify";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import { FaPaperPlane } from "react-icons/fa";

/* ——— Sous-bloc d’une question ——— */
const QuestionBlock = ({ q, value, onChange }) => {
  return (
    <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-4">
      <div
        className="mb-3 font-medium text-slate-900 dark:text-white"
        dangerouslySetInnerHTML={{ __html: q.question_text }}
      />

      {q.type === "open" && (
        <input
          type="text"
          placeholder="Votre réponse"
          value={value || ""}
          onChange={(e) => onChange(q.id, e.target.value, q.type)}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2"
        />
      )}

      {q.type === "single" && (
        <div className="grid gap-2">
          {(q.choices || []).map((choice) => (
            <label key={choice.id} className="flex items-center gap-2">
              <input
                type="radio"
                name={`question_${q.id}`}
                value={choice.id}
                checked={value === choice.id}
                onChange={() => onChange(q.id, choice.id, q.type)}
                className="h-4 w-4"
              />
              <span className="text-sm">{choice.choice_text}</span>
            </label>
          ))}
        </div>
      )}

      {q.type === "multiple" && (
        <div className="grid gap-2">
          {(q.choices || []).map((choice) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(choice.id);
            return (
              <label key={choice.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  value={choice.id}
                  checked={checked}
                  onChange={() => onChange(q.id, choice.id, q.type)}
                  className="h-4 w-4"
                />
                <span className="text-sm">{choice.choice_text}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ParticipateVoteModal = ({
  isOpen,
  onClose,
  voteId,
  refreshVotes,
  userId,
}) => {
  const [vote, setVote] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Charge le vote + reset des réponses à l'ouverture
  useEffect(() => {
    if (!isOpen || !voteId) {
      setVote(null);
      setResponses({});
      setSubmitting(false);
      return;
    }

    // Vérifier la présence du token avant tout
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Votre session a expiré. Veuillez vous reconnecter.");
      onClose?.();
      return;
    }

    (async () => {
      try {
        const data = await getVoteById(voteId);
        setVote(data);
        setResponses({});
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement du vote");
        onClose?.();
      }
    })();
  }, [isOpen, voteId, onClose]);

  const handleChange = (questionId, value, type) => {
    if (type === "multiple") {
      setResponses((prev) => {
        const prevArr = Array.isArray(prev[questionId]) ? prev[questionId] : [];
        const exists = prevArr.includes(value);
        return {
          ...prev,
          [questionId]: exists
            ? prevArr.filter((v) => v !== value)
            : [...prevArr, value],
        };
      });
    } else {
      setResponses((prev) => ({ ...prev, [questionId]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!voteId || !userId) return;
    const payload = { vote_id: voteId, user_id: userId, responses };

    try {
      setSubmitting(true);
      const ok = await submitVote(payload);
      if (!ok) throw new Error("Échec de la soumission");
      toast.success("Réponses enregistrées !");
      onClose?.();
      refreshVotes?.();
    } catch (e) {
      console.error("❌ Erreur soumission vote :", e);
      toast.error("Erreur lors de l'envoi des réponses");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !vote) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="vote-participate-title"
      describedById="vote-participate-desc"
      initialFocusSelector="#send-btn"
    >
      <ModalHeader
        id="vote-participate-title"
        title={vote.title}
        subtitle={vote.description}
        onClose={onClose}
      />
      <ModalBody id="vote-participate-desc">
        {/* Info mode anonyme / nominatif */}
        <div className="mb-4 rounded-xl bg-slate-50 dark:bg-slate-800 px-3 py-2 text-xs text-slate-700 dark:text-slate-200">
          <span className="font-semibold">
            {vote.type === "sondage" ? "Sondage" : "Vote"}{" "}
            {vote.is_anonymous === false ? "nominatif" : "anonyme"}
          </span>
          <span className="block mt-1">
            {vote.is_anonymous === false
              ? "Vos réponses seront associées à votre identité et pourront être consultées par les instances de la ligue (par exemple pour les délibérations)."
              : "Vos réponses seront prises en compte sans que votre identité apparaisse dans les résultats consultés par le public."}
          </span>
        </div>

        <div className="space-y-4">
          {vote.questions?.length ? (
            vote.questions.map((q) => (
              <QuestionBlock
                key={q.id}
                q={q}
                value={responses[q.id]}
                onChange={handleChange}
              />
            ))
          ) : (
            <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-4 text-slate-600 dark:text-slate-300">
              Aucune question configurée pour ce vote.
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <button
          onClick={onClose}
          className="inline-flex items-center px-4 h-10 rounded-lg ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-800"
          disabled={submitting}
        >
          Annuler
        </button>
        <button
          id="send-btn"
          onClick={handleSubmit}
          disabled={submitting}
          className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg ${
            submitting
              ? "bg-indigo-300 cursor-wait"
              : "bg-indigo-600 hover:bg-indigo-700"
          } text-white`}
        >
          <FaPaperPlane />
          {submitting ? "Envoi..." : "Envoyer"}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ParticipateVoteModal;
